import { useCallback, useEffect, useState } from "react";

// ── Types ─────────────────────────────────────────────────────

type TaskCategory =
  | "CodeGeneration"
  | "Debugging"
  | "Explanation"
  | "Refactoring"
  | "TestWriting";

type BenchmarkTask = {
  id: string;
  name: string;
  prompt: string;
  expected_contains: string[];
  category: TaskCategory;
};

type BenchmarkResult = {
  task_id: string;
  model: string;
  provider: string;
  output: string;
  tokens_in: number;
  tokens_out: number;
  duration_ms: number;
  passed: boolean;
  score: number;
};

type ModelScore = {
  model: string;
  avg_score: number;
  avg_duration_ms: number;
  tasks_passed: number;
  total_tasks: number;
};

// ── Constants ─────────────────────────────────────────────────

const CATEGORY_COLORS: Record<TaskCategory, string> = {
  CodeGeneration: "#50fa7b",
  Debugging: "#ff5555",
  Explanation: "#8be9fd",
  Refactoring: "#f1fa8c",
  TestWriting: "#bd93f9",
};

const CATEGORY_LABELS: Record<TaskCategory, string> = {
  CodeGeneration: "Code Gen",
  Debugging: "Debug",
  Explanation: "Explain",
  Refactoring: "Refactor",
  TestWriting: "Testing",
};

// ── Built-in tasks (mirrors Rust BenchmarkSuite::builtin_tasks) ──

const BUILTIN_TASKS: BenchmarkTask[] = [
  {
    id: "bench-01",
    name: "Fibonacci in Rust",
    prompt: "Write a fibonacci function in Rust",
    expected_contains: ["fn", "fibonacci"],
    category: "CodeGeneration",
  },
  {
    id: "bench-02",
    name: "Fix off-by-one bug",
    prompt: "Fix the bug in this code — off-by-one error in the loop",
    expected_contains: ["..="],
    category: "Debugging",
  },
  {
    id: "bench-03",
    name: "Explain regex",
    prompt: "Explain what this regex does: ^[a-zA-Z0-9_.+-]+@",
    expected_contains: ["email"],
    category: "Explanation",
  },
  {
    id: "bench-04",
    name: "Refactor to iterators",
    prompt: "Refactor this Rust code to use iterators instead of a manual loop",
    expected_contains: [".iter()", ".map(", ".collect()"],
    category: "Refactoring",
  },
  {
    id: "bench-05",
    name: "Write unit test",
    prompt: "Write a unit test for add(a, b)",
    expected_contains: ["#[test]", "assert"],
    category: "TestWriting",
  },
  {
    id: "bench-06",
    name: "TypeScript User interface",
    prompt: "Generate a TypeScript interface for User",
    expected_contains: ["interface", "User"],
    category: "CodeGeneration",
  },
  {
    id: "bench-07",
    name: "SQL injection detection",
    prompt: "What's wrong with: SELECT * FROM users WHERE id = ' + input",
    expected_contains: ["injection"],
    category: "Debugging",
  },
  {
    id: "bench-08",
    name: "Callback to async/await",
    prompt: "Convert this callback to async/await",
    expected_contains: ["async", "await"],
    category: "Refactoring",
  },
  {
    id: "bench-09",
    name: "Rust Dockerfile",
    prompt: "Write a Dockerfile for a Rust web app",
    expected_contains: ["FROM", "COPY", "cargo"],
    category: "CodeGeneration",
  },
  {
    id: "bench-10",
    name: "Big-O of nested loops",
    prompt: "Explain Big-O of nested loops",
    expected_contains: ["O(n", "quadratic"],
    category: "Explanation",
  },
];

// ── Helpers ───────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function scoreColor(score: number): string {
  if (score >= 0.8) return "#50fa7b";
  if (score >= 0.5) return "#f1fa8c";
  return "#ff5555";
}

function scoreBar(score: number): string {
  const filled = Math.round(score * 10);
  const empty = 10 - filled;
  return "\u2588".repeat(filled) + "\u2591".repeat(empty);
}

// ── Component ─────────────────────────────────────────────────

export function BenchmarkWorkbench() {
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<BenchmarkResult[]>([]);
  const [leaderboard, setLeaderboard] = useState<ModelScore[]>([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentTask: "", currentModel: "" });
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [view, setView] = useState<"leaderboard" | "tasks">("leaderboard");

  // Fetch available models from Ollama
  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch("/api/providers");
      const data = await res.json();
      // Extract models from providers — look for ollama or any provider with models
      if (data.providers) {
        const models: string[] = [];
        for (const p of data.providers) {
          if (p.models) {
            models.push(...p.models);
          }
        }
        if (models.length > 0) {
          setAvailableModels(models);
        } else {
          // Fallback: try ollama tags directly
          try {
            const ollamaRes = await fetch("http://localhost:11434/api/tags");
            const ollamaData = await ollamaRes.json();
            if (ollamaData.models) {
              setAvailableModels(ollamaData.models.map((m: { name: string }) => m.name));
            }
          } catch {
            // Ollama not available
          }
        }
      }
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const toggleModel = (model: string) => {
    setSelectedModels((prev) => {
      const next = new Set(prev);
      if (next.has(model)) {
        next.delete(model);
      } else {
        next.add(model);
      }
      return next;
    });
  };

  // Run benchmark — sends tasks to backend via /api/chat and scores locally
  const runBenchmark = async () => {
    if (selectedModels.size === 0) return;

    setRunning(true);
    setResults([]);
    setLeaderboard([]);

    const models = Array.from(selectedModels);
    const tasks = BUILTIN_TASKS;
    const total = models.length * tasks.length;
    const allResults: BenchmarkResult[] = [];

    let current = 0;

    for (const model of models) {
      for (const task of tasks) {
        current++;
        setProgress({ current, total, currentTask: task.name, currentModel: model });

        try {
          // Use the chat API to run the task
          const startTime = Date.now();

          const res = await fetch("/api/chat", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              prompt: task.prompt,
              model: model,
              system: "You are a helpful coding assistant. Be concise and correct.",
            }),
          });

          const chatData = await res.json();

          // If streaming, collect events from SSE
          let output = "";
          if (chatData.streaming) {
            output = await collectStreamedOutput();
          } else if (chatData.error) {
            output = `[ERROR: ${chatData.error}]`;
          }

          const duration = Date.now() - startTime;
          const lowerOutput = output.toLowerCase();

          // Score: fraction of expected keywords found
          const matched = task.expected_contains.filter((kw) =>
            lowerOutput.includes(kw.toLowerCase())
          ).length;
          const score = matched / Math.max(task.expected_contains.length, 1);
          const passed = score >= 0.5;

          const result: BenchmarkResult = {
            task_id: task.id,
            model,
            provider: "ollama",
            output,
            tokens_in: Math.floor(task.prompt.length / 4),
            tokens_out: Math.floor(output.length / 4),
            duration_ms: duration,
            passed,
            score,
          };

          allResults.push(result);
          setResults([...allResults]);
        } catch (err) {
          allResults.push({
            task_id: task.id,
            model,
            provider: "ollama",
            output: `[ERROR: ${err}]`,
            tokens_in: 0,
            tokens_out: 0,
            duration_ms: 0,
            passed: false,
            score: 0,
          });
          setResults([...allResults]);
        }
      }
    }

    // Compute leaderboard
    const board = computeLeaderboard(allResults);
    setLeaderboard(board);
    setRunning(false);
  };

  // Collect streamed text from SSE endpoint
  const collectStreamedOutput = (): Promise<string> => {
    return new Promise((resolve) => {
      const eventSource = new EventSource("/api/events");
      let text = "";
      const timeout = setTimeout(() => {
        eventSource.close();
        resolve(text);
      }, 120000); // 2 minute timeout

      eventSource.addEventListener("agent-text", (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          if (data.text) text += data.text;
        } catch {
          // ignore parse errors
        }
      });

      eventSource.addEventListener("agent-done", () => {
        clearTimeout(timeout);
        eventSource.close();
        resolve(text);
      });

      eventSource.addEventListener("agent-error", (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          text += `\n[ERROR: ${data.error}]`;
        } catch {
          // ignore
        }
        clearTimeout(timeout);
        eventSource.close();
        resolve(text);
      });
    });
  };

  const computeLeaderboard = (allResults: BenchmarkResult[]): ModelScore[] => {
    const byModel = new Map<string, BenchmarkResult[]>();
    for (const r of allResults) {
      if (!byModel.has(r.model)) byModel.set(r.model, []);
      byModel.get(r.model)!.push(r);
    }

    const scores: ModelScore[] = [];
    for (const [model, modelResults] of byModel) {
      const totalTasks = modelResults.length;
      const tasksPassed = modelResults.filter((r) => r.passed).length;
      const avgScore = totalTasks > 0
        ? modelResults.reduce((sum, r) => sum + r.score, 0) / totalTasks
        : 0;
      const avgDuration = totalTasks > 0
        ? Math.round(modelResults.reduce((sum, r) => sum + r.duration_ms, 0) / totalTasks)
        : 0;

      scores.push({
        model,
        avg_score: avgScore,
        avg_duration_ms: avgDuration,
        tasks_passed: tasksPassed,
        total_tasks: totalTasks,
      });
    }

    scores.sort((a, b) => {
      const scoreDiff = b.avg_score - a.avg_score;
      if (Math.abs(scoreDiff) > 0.001) return scoreDiff;
      return a.avg_duration_ms - b.avg_duration_ms;
    });

    return scores;
  };

  // Get results for a specific task across all models
  const taskResults = (taskId: string): BenchmarkResult[] =>
    results.filter((r) => r.task_id === taskId);

  const selectedTask = selectedTaskId
    ? BUILTIN_TASKS.find((t) => t.id === selectedTaskId) ?? null
    : null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>
            <span style={styles.titleIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#50fa7b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20V10" />
                <path d="M18 20V4" />
                <path d="M6 20v-4" />
              </svg>
            </span>
            Model Benchmark
          </h2>
          {results.length > 0 && (
            <span style={styles.resultCount}>
              {results.length} results
            </span>
          )}
        </div>
        <div style={styles.headerRight}>
          <div style={styles.viewToggle}>
            <button
              style={{
                ...styles.viewBtn,
                ...(view === "leaderboard" ? styles.viewBtnActive : {}),
              }}
              onClick={() => setView("leaderboard")}
            >
              Leaderboard
            </button>
            <button
              style={{
                ...styles.viewBtn,
                ...(view === "tasks" ? styles.viewBtnActive : {}),
              }}
              onClick={() => setView("tasks")}
            >
              Tasks
            </button>
          </div>
        </div>
      </div>

      {/* Model selection + run */}
      <div style={styles.controlPanel}>
        <div style={styles.modelSection}>
          <span style={styles.sectionLabel}>MODELS</span>
          <div style={styles.modelGrid}>
            {availableModels.length === 0 && (
              <span style={styles.emptyHint}>
                No models found. Is Ollama running?
              </span>
            )}
            {availableModels.map((model) => (
              <button
                key={model}
                style={{
                  ...styles.modelChip,
                  ...(selectedModels.has(model) ? styles.modelChipSelected : {}),
                }}
                onClick={() => toggleModel(model)}
                disabled={running}
              >
                {selectedModels.has(model) ? "\u2713 " : ""}
                {model}
              </button>
            ))}
          </div>
        </div>
        <button
          style={{
            ...styles.runBtn,
            ...(running || selectedModels.size === 0 ? styles.runBtnDisabled : {}),
          }}
          onClick={runBenchmark}
          disabled={running || selectedModels.size === 0}
        >
          {running ? "Running..." : "Run Benchmark"}
        </button>
      </div>

      {/* Progress bar */}
      {running && (
        <div style={styles.progressContainer}>
          <div style={styles.progressHeader}>
            <span style={styles.progressLabel}>
              {progress.currentModel} / {progress.currentTask}
            </span>
            <span style={styles.progressCount}>
              {progress.current}/{progress.total}
            </span>
          </div>
          <div style={styles.progressBarBg}>
            <div
              style={{
                ...styles.progressBarFill,
                width: `${(progress.current / Math.max(progress.total, 1)) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Leaderboard view */}
      {view === "leaderboard" && leaderboard.length > 0 && (
        <div style={styles.leaderboardContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={{ ...styles.th, textAlign: "left" as const }}>Model</th>
                <th style={styles.th}>Score</th>
                <th style={styles.th}>Passed</th>
                <th style={styles.th}>Avg Speed</th>
                <th style={styles.th}>Rating</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, i) => (
                <tr key={entry.model} style={styles.tr}>
                  <td style={styles.tdRank}>
                    {i === 0 ? "\u{1F947}" : i === 1 ? "\u{1F948}" : i === 2 ? "\u{1F949}" : `${i + 1}`}
                  </td>
                  <td style={styles.tdModel}>{entry.model}</td>
                  <td style={{ ...styles.td, color: scoreColor(entry.avg_score) }}>
                    {(entry.avg_score * 100).toFixed(0)}%
                  </td>
                  <td style={styles.td}>
                    {entry.tasks_passed}/{entry.total_tasks}
                  </td>
                  <td style={styles.td}>
                    {formatDuration(entry.avg_duration_ms)}
                  </td>
                  <td style={{ ...styles.td, fontFamily: "monospace", color: scoreColor(entry.avg_score), letterSpacing: "1px" }}>
                    {scoreBar(entry.avg_score)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Leaderboard empty state */}
      {view === "leaderboard" && leaderboard.length === 0 && !running && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20V10" />
              <path d="M18 20V4" />
              <path d="M6 20v-4" />
            </svg>
          </div>
          <p style={styles.emptyText}>
            Select models above and run a benchmark to see the leaderboard.
          </p>
          <p style={styles.emptySubtext}>
            10 coding tasks across code generation, debugging, explanation, refactoring, and test writing.
          </p>
        </div>
      )}

      {/* Tasks view */}
      {view === "tasks" && (
        <div style={styles.tasksContainer}>
          <div style={styles.taskList}>
            {BUILTIN_TASKS.map((task) => {
              const tr = taskResults(task.id);
              const hasResults = tr.length > 0;
              const avgScore = hasResults
                ? tr.reduce((s, r) => s + r.score, 0) / tr.length
                : -1;

              return (
                <div
                  key={task.id}
                  style={{
                    ...styles.taskCard,
                    ...(selectedTaskId === task.id ? styles.taskCardSelected : {}),
                  }}
                  onClick={() =>
                    setSelectedTaskId(selectedTaskId === task.id ? null : task.id)
                  }
                >
                  <div style={styles.taskCardTop}>
                    <span
                      style={{
                        ...styles.categoryBadge,
                        backgroundColor: CATEGORY_COLORS[task.category] + "18",
                        color: CATEGORY_COLORS[task.category],
                      }}
                    >
                      {CATEGORY_LABELS[task.category]}
                    </span>
                    {hasResults && (
                      <span
                        style={{
                          ...styles.taskScore,
                          color: scoreColor(avgScore),
                        }}
                      >
                        {(avgScore * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <div style={styles.taskName}>{task.name}</div>
                  <div style={styles.taskPrompt}>{task.prompt}</div>
                  {hasResults && (
                    <div style={styles.taskKeywords}>
                      expects: {task.expected_contains.join(", ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Task detail panel */}
          {selectedTask && (
            <div style={styles.taskDetail}>
              <div style={styles.taskDetailHeader}>
                <h3 style={styles.taskDetailTitle}>{selectedTask.name}</h3>
                <span
                  style={{
                    ...styles.categoryBadge,
                    backgroundColor: CATEGORY_COLORS[selectedTask.category] + "18",
                    color: CATEGORY_COLORS[selectedTask.category],
                  }}
                >
                  {CATEGORY_LABELS[selectedTask.category]}
                </span>
              </div>
              <div style={styles.taskDetailPrompt}>
                <span style={styles.detailLabel}>PROMPT</span>
                <pre style={styles.codePre}>{selectedTask.prompt}</pre>
              </div>
              <div style={styles.taskDetailExpected}>
                <span style={styles.detailLabel}>EXPECTED KEYWORDS</span>
                <div style={styles.keywordRow}>
                  {selectedTask.expected_contains.map((kw) => (
                    <span key={kw} style={styles.keywordChip}>
                      {kw}
                    </span>
                  ))}
                </div>
              </div>

              {/* Per-model results for this task */}
              {taskResults(selectedTask.id).length > 0 && (
                <div style={styles.taskDetailResults}>
                  <span style={styles.detailLabel}>MODEL OUTPUTS</span>
                  {taskResults(selectedTask.id).map((r) => (
                    <div key={r.model} style={styles.modelOutputCard}>
                      <div style={styles.modelOutputHeader}>
                        <span style={styles.modelOutputName}>{r.model}</span>
                        <span
                          style={{
                            ...styles.passBadge,
                            backgroundColor: r.passed ? "#50fa7b18" : "#ff555518",
                            color: r.passed ? "#50fa7b" : "#ff5555",
                          }}
                        >
                          {r.passed ? "PASS" : "FAIL"}
                        </span>
                        <span style={styles.modelOutputMeta}>
                          {(r.score * 100).toFixed(0)}% | {formatDuration(r.duration_ms)}
                        </span>
                      </div>
                      <pre style={styles.outputPre}>{r.output || "(no output)"}</pre>
                    </div>
                  ))}
                </div>
              )}

              {taskResults(selectedTask.id).length === 0 && (
                <div style={styles.noResults}>
                  No results yet. Run a benchmark to see model outputs.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Inline styles (dark terminal theme) ───────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: "24px",
    minHeight: "100%",
    backgroundColor: "#0d0d0d",
    color: "#f8f8f2",
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 600,
    color: "#f8f8f2",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  titleIcon: {
    display: "flex",
    alignItems: "center",
  },
  resultCount: {
    fontSize: "12px",
    color: "#6272a4",
    backgroundColor: "#1e1e2e",
    padding: "2px 8px",
    borderRadius: "4px",
  },
  viewToggle: {
    display: "flex",
    gap: "2px",
    backgroundColor: "#1e1e2e",
    borderRadius: "6px",
    padding: "2px",
  },
  viewBtn: {
    background: "none",
    border: "none",
    color: "#6272a4",
    fontSize: "12px",
    padding: "5px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s",
  },
  viewBtnActive: {
    backgroundColor: "#282a36",
    color: "#50fa7b",
  },

  // Control panel
  controlPanel: {
    backgroundColor: "#1a1b26",
    border: "1px solid #282a36",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "20px",
    display: "flex",
    alignItems: "flex-end",
    gap: "16px",
  },
  modelSection: {
    flex: 1,
  },
  sectionLabel: {
    display: "block",
    fontSize: "10px",
    fontWeight: 600,
    color: "#6272a4",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: "8px",
  },
  modelGrid: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap" as const,
  },
  modelChip: {
    background: "none",
    border: "1px solid rgba(255,255,255,0.06)",
    color: "#a8b2b8",
    fontSize: "12px",
    padding: "5px 12px",
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s",
  },
  modelChipSelected: {
    borderColor: "#50fa7b",
    color: "#50fa7b",
    backgroundColor: "#50fa7b10",
  },
  emptyHint: {
    fontSize: "12px",
    color: "#6272a4",
    fontStyle: "italic" as const,
  },
  runBtn: {
    backgroundColor: "#50fa7b",
    border: "none",
    color: "#0d0d0d",
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 24px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
  },
  runBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },

  // Progress
  progressContainer: {
    backgroundColor: "#1a1b26",
    border: "1px solid #282a36",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "20px",
  },
  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  progressLabel: {
    fontSize: "12px",
    color: "#a8b2b8",
  },
  progressCount: {
    fontSize: "12px",
    color: "#6272a4",
  },
  progressBarBg: {
    height: "4px",
    backgroundColor: "#282a36",
    borderRadius: "2px",
    overflow: "hidden" as const,
  },
  progressBarFill: {
    height: "100%",
    backgroundColor: "#50fa7b",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },

  // Leaderboard
  leaderboardContainer: {
    backgroundColor: "#1a1b26",
    border: "1px solid #282a36",
    borderRadius: "8px",
    overflow: "hidden" as const,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "13px",
  },
  th: {
    padding: "12px 16px",
    textAlign: "center" as const,
    fontSize: "10px",
    fontWeight: 600,
    color: "#6272a4",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    borderBottom: "1px solid #282a36",
    backgroundColor: "#0d0d0d",
  },
  tr: {
    borderBottom: "1px solid #1e1e2e",
  },
  tdRank: {
    padding: "12px 16px",
    textAlign: "center" as const,
    fontSize: "14px",
  },
  tdModel: {
    padding: "12px 16px",
    fontWeight: 500,
    color: "#f8f8f2",
  },
  td: {
    padding: "12px 16px",
    textAlign: "center" as const,
  },

  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 24px",
  },
  emptyIcon: {
    marginBottom: "16px",
    opacity: 0.5,
  },
  emptyText: {
    margin: "0 0 8px",
    fontSize: "14px",
    color: "#6272a4",
  },
  emptySubtext: {
    margin: 0,
    fontSize: "12px",
    color: "#6272a4",
  },

  // Tasks view
  tasksContainer: {
    display: "grid",
    gridTemplateColumns: "340px 1fr",
    gap: "16px",
    minHeight: "400px",
  },
  taskList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    overflowY: "auto" as const,
    maxHeight: "calc(100vh - 280px)",
  },
  taskCard: {
    backgroundColor: "#1a1b26",
    border: "1px solid #282a36",
    borderRadius: "6px",
    padding: "12px",
    cursor: "pointer",
    transition: "border-color 0.15s",
  },
  taskCardSelected: {
    borderColor: "#50fa7b",
  },
  taskCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
  },
  categoryBadge: {
    fontSize: "10px",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: "3px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
  },
  taskScore: {
    fontSize: "13px",
    fontWeight: 600,
  },
  taskName: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#f8f8f2",
    marginBottom: "4px",
  },
  taskPrompt: {
    fontSize: "11px",
    color: "#6272a4",
    lineHeight: 1.4,
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
    whiteSpace: "nowrap" as const,
  },
  taskKeywords: {
    fontSize: "10px",
    color: "#6272a4",
    marginTop: "6px",
    fontStyle: "italic" as const,
  },

  // Task detail panel
  taskDetail: {
    backgroundColor: "#1a1b26",
    border: "1px solid #282a36",
    borderRadius: "8px",
    padding: "20px",
    overflowY: "auto" as const,
    maxHeight: "calc(100vh - 280px)",
  },
  taskDetailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  taskDetailTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "#f8f8f2",
  },
  taskDetailPrompt: {
    marginBottom: "16px",
  },
  taskDetailExpected: {
    marginBottom: "16px",
  },
  detailLabel: {
    display: "block",
    fontSize: "10px",
    fontWeight: 600,
    color: "#6272a4",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  codePre: {
    margin: 0,
    backgroundColor: "#0d0d0d",
    border: "1px solid #282a36",
    borderRadius: "4px",
    padding: "10px",
    fontSize: "12px",
    color: "#50fa7b",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    fontFamily: "inherit",
  },
  keywordRow: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap" as const,
  },
  keywordChip: {
    fontSize: "11px",
    color: "#8be9fd",
    backgroundColor: "#8be9fd12",
    padding: "2px 8px",
    borderRadius: "3px",
    border: "1px solid #8be9fd25",
  },

  // Model output cards within task detail
  taskDetailResults: {
    marginTop: "16px",
  },
  modelOutputCard: {
    backgroundColor: "#1e1e2e",
    border: "1px solid #282a36",
    borderRadius: "6px",
    padding: "12px",
    marginBottom: "10px",
  },
  modelOutputHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "8px",
  },
  modelOutputName: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#f8f8f2",
  },
  passBadge: {
    fontSize: "10px",
    fontWeight: 700,
    padding: "2px 6px",
    borderRadius: "3px",
    letterSpacing: "0.5px",
  },
  modelOutputMeta: {
    fontSize: "11px",
    color: "#6272a4",
    marginLeft: "auto",
  },
  outputPre: {
    margin: 0,
    backgroundColor: "#0d0d0d",
    border: "1px solid #282a36",
    borderRadius: "4px",
    padding: "10px",
    fontSize: "11px",
    color: "#f8f8f2",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    fontFamily: "inherit",
    maxHeight: "200px",
    overflowY: "auto" as const,
  },
  noResults: {
    textAlign: "center" as const,
    padding: "40px 16px",
    color: "#6272a4",
    fontSize: "12px",
    fontStyle: "italic" as const,
  },
};
